import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineLock } from 'react-icons/ai';
import { BsPlayFill, BsCheckCircle } from 'react-icons/bs';
import { VideoItemProps } from '../../interfaces/components/videoItem';

/**
 * VideoItem - A component that displays a video item with various states (locked, playing, completed)
 *
 * @component
 * @example
 * ```tsx
 * <VideoItem
 *   title="Introduction to Course"
 *   duration="5:30"
 *   imageSource={{ uri: '/path/to/thumbnail.jpg' }}
 *   onPress={() => console.log('Video clicked')}
 *   locked={false}
 *   completed={false}
 *   isPlaying={false}
 * />
 * ```
 */
const VideoItem: React.FC<VideoItemProps> = ({
  title,
  duration,
  imageSource,
  onPress,
  locked,
  completed,
  isPlaying,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center p-3 border-l-4 cursor-pointer transition-colors 
        ${isPlaying ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-transparent'} 
        ${completed ? 'hover:bg-green-50/30 dark:hover:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}
        ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
      onClick={locked ? undefined : onPress}
      role="button"
      aria-disabled={locked}
      aria-label={locked ? t('content.videoLocked', { title }) : t('content.playVideo', { title })}
    >
      <div className="relative mr-3 flex-shrink-0">
        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <img
            src={imageSource.uri}
            alt={title}
            className={`w-full h-full object-cover transition-opacity duration-300 ${completed ? 'opacity-80' : ''}`}
          />
        </div>
        <div
          className={`absolute inset-0 flex items-center justify-center transition-colors
          ${
            completed
              ? 'bg-green-500/10 dark:bg-green-500/20'
              : isPlaying
                ? 'bg-primary/10 dark:bg-primary/20'
                : 'bg-black/20 dark:bg-black/40'
          }`}
        >
          {locked ? (
            <AiOutlineLock size={16} className="text-gray-600 dark:text-gray-400" />
          ) : completed ? (
            <BsCheckCircle size={16} className="text-green-500" />
          ) : (
            <BsPlayFill size={16} className="text-gray-700 dark:text-gray-300" />
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-titleText dark:text-gray-100 text-sm truncate">{title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">{duration}</p>
      </div>
      {isPlaying && (
        <div className="ml-2 flex-shrink-0">
          <div className="bg-primary/10 dark:bg-primary/20 text-primary text-xs px-2 py-1 rounded-lg whitespace-nowrap">
            {t('courseDetails.content.playing')}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoItem;
